import { z } from 'zod';
import {
  TILE_TYPES,
  TILE_SIZES,
  TIERS,
  PROVIDER_IDS,
  TILE_SCHEMA_VERSION,
  TILE_ID_PATTERN,
  MAX_LABEL_LENGTH,
  type TileType,
} from './constants.js';
import { glanceMapSchema } from './glance.js';
import { getTileTypeDef } from './registry.js';
import { isMetricOf } from './data-shapes.js';

/**
 * H0 — the tile config schema (ADR-002 §3, §4, §7).
 *
 * A tile is declarative config, never bespoke code (D-002). This schema is a
 * SECURITY CONTROL: cloned/imported tiles are untrusted input authored by
 * strangers (ADR-002 §7). Guarantees enforced here:
 *   - `.strict()` everywhere → no unknown keys; blocks URL/host smuggling (#1)
 *     and prototype-pollution keys (#5).
 *   - `provider` ∈ the type's registry allowlist → SSRF firewall (#1).
 *   - `params` validated by the type's own schema → bounded fan-out (#4).
 *   - `glance.*` must name real metrics of the type (§3.3).
 *   - `refresh.intervalSec ≥ the type's TTL floor` → cost floor (#4).
 *   - `tier` is FORCED to the registry value → no entitlement bypass (#3).
 *   - `label` is length-capped plain text (#2); rendered as text, never HTML.
 *
 * The parse result is a fresh immutable object (parse, don't mutate — global rule).
 */

/** `source` sub-object. NO url/host/path/header/template fields — ever (ADR-002 §7 #1). */
const sourceSchema = z
  .object({
    provider: z.enum(PROVIDER_IDS),
    // params shape is refined per-type below; accept an object here, validate in superRefine.
    params: z.record(z.unknown()),
  })
  .strict();

const refreshSchema = z
  .object({
    intervalSec: z.number().int().positive(),
  })
  .strict();

const displaySchema = z
  .object({
    size: z.enum(TILE_SIZES),
    variant: z.string().min(1),
  })
  .strict();

/**
 * Raw tile shape before cross-field refinement. Kept `.strict()` so a clone cannot
 * smuggle extra top-level fields past the parser (ADR-002 §4).
 */
const tileBaseSchema = z
  .object({
    v: z.literal(TILE_SCHEMA_VERSION),
    id: z.string().regex(TILE_ID_PATTERN),
    type: z.enum(TILE_TYPES),
    source: sourceSchema.optional(),
    refresh: refreshSchema.optional(),
    display: displaySchema,
    glance: glanceMapSchema,
    // `tier` is accepted but IGNORED — the registry value is authoritative (#3).
    tier: z.enum(TIERS).optional(),
    label: z.string().max(MAX_LABEL_LENGTH).optional(),
  })
  .strict();

/** Validate a glance slot references a real metric of the tile type (§3.3). */
function checkGlanceTarget(
  ctx: z.RefinementCtx,
  type: TileType,
  slot: 'primary' | 'delta' | 'state',
  key: string | null,
): void {
  if (key === null) return;
  if (!isMetricOf(type, key)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['glance', slot],
      message: `glance.${slot} "${key}" is not a metric of ${type}`,
    });
  }
}

/**
 * Validate `source.params` against the type's strict schema and surface each
 * failure at the right path. This is where the SSRF firewall actually bites
 * (ADR-002 §7 #1): the base `sourceSchema.params` is `z.record(z.unknown())`, so
 * WITHOUT this call a URL/host/header could sit unvalidated in params. It runs for
 * BOTH no-fetch and fetch tiles — every tile with a params schema is checked.
 */
function checkParams(
  ctx: z.RefinementCtx,
  paramsSchema: z.ZodTypeAny,
  params: unknown,
): void {
  const parsed = paramsSchema.safeParse(params);
  if (parsed.success) return;
  for (const issue of parsed.error.issues) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['source', 'params', ...issue.path],
      message: issue.message,
    });
  }
}

/**
 * The full tile schema: base shape + registry-driven cross-field rules, then a
 * transform that forces `tier` from the registry.
 */
export const TileSchema = tileBaseSchema
  .superRefine((tile, ctx) => {
    const def = getTileTypeDef(tile.type);

    // --- display.variant / size must be legal for the type ---
    if (!def.allowedVariants.includes(tile.display.variant)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['display', 'variant'],
        message: `variant "${tile.display.variant}" not allowed for ${tile.type}`,
      });
    }
    if (!def.allowedSizes.includes(tile.display.size)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['display', 'size'],
        message: `size "${tile.display.size}" not allowed for ${tile.type}`,
      });
    }

    // --- glance targets must be real metrics of the type (§3.3) ---
    checkGlanceTarget(ctx, tile.type, 'primary', tile.glance.primary);
    checkGlanceTarget(ctx, tile.type, 'delta', tile.glance.delta);
    checkGlanceTarget(ctx, tile.type, 'state', tile.glance.state);

    // --- no-fetch tiles (clock): source/refresh optional; if source present,
    //     provider must be 'local'. Params are STILL validated when present. ---
    if (def.noFetch) {
      if (tile.source) {
        if (tile.source.provider !== 'local') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['source', 'provider'],
            message: `${tile.type} is a no-fetch tile; provider must be 'local' or omitted`,
          });
        }
        // Validate params even for no-fetch tiles so garbage/hostile keys are
        // rejected at the boundary (ADR-002 §7 — .strict() must actually bite).
        if (def.paramsSchema) {
          checkParams(ctx, def.paramsSchema, tile.source.params);
        }
      }
      return;
    }

    // --- fetch tiles: source is required ---
    if (!tile.source) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['source'],
        message: `${tile.type} requires a source`,
      });
      return; // nothing more to validate without a source
    }

    // FAIL CLOSED: a fetch tile whose registry entry has no params schema cannot
    // be safely validated — its params would be an unchecked `z.record(unknown)`,
    // the exact hole the SSRF firewall must not have (ADR-002 §7 #1). Reject rather
    // than pass unvalidated params. (Guards against a future registry edit that
    // adds a provider without a params schema.)
    if (def.paramsSchema === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['type'],
        message: `${tile.type} has no params schema registered; cannot validate a fetch tile safely`,
      });
      return;
    }

    // --- provider allowlist per type (SSRF firewall #1) ---
    if (!def.allowedProviders.includes(tile.source.provider)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['source', 'provider'],
        message: `provider "${tile.source.provider}" not allowed for ${tile.type}`,
      });
    }

    // --- per-type params validation (bounded fan-out #4) ---
    checkParams(ctx, def.paramsSchema, tile.source.params);

    // --- refresh cadence window (cost floor #4): never faster than the cache
    //     refills (min = ttlSecFloor) and not absurdly slow (max from registry). ---
    if (tile.refresh) {
      const [, max] = def.refreshBounds;
      if (tile.refresh.intervalSec < def.ttlSecFloor) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['refresh', 'intervalSec'],
          message: `intervalSec ${tile.refresh.intervalSec} is below the ${def.ttlSecFloor}s TTL floor for ${tile.type}`,
        });
      }
      if (tile.refresh.intervalSec > max) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['refresh', 'intervalSec'],
          message: `intervalSec ${tile.refresh.intervalSec} exceeds the ${max}s max for ${tile.type}`,
        });
      }
    }
  })
  .transform((tile) => {
    // Force the authoritative tier from the registry (entitlement bypass #3).
    // Returns a fresh object — never mutate the parsed input.
    const def = getTileTypeDef(tile.type);
    return { ...tile, tier: def.tier } as const;
  });

/** Inferred output type — the validated, tier-corrected tile (immutable readonly). */
export type Tile = Readonly<z.infer<typeof TileSchema>>;

/** Throwing parse — for trusted call sites that want an exception on bad input. */
export function parseTile(input: unknown): Tile {
  return TileSchema.parse(input);
}

/** Non-throwing parse — for boundaries that degrade gracefully (ADR-002 §4). */
export function safeParseTile(input: unknown): z.SafeParseReturnType<unknown, Tile> {
  return TileSchema.safeParse(input);
}
